/**
 * Кассын төлбөрийн хэсэг — нэг эсвэл ХОЁР хэлбэр.
 *
 * Бодит тохиолдол: «500,000₮ бэлнээр, үлдсэнийг LendMN-ээр». Гар
 * утасны POS-ын `payment-picker.tsx`-тай ижил дүрэм, ижил чип.
 *
 * ⚠️ ХОЁР ДАХЬ ХЭСГИЙН ДҮНГ ХҮН ОРУУЛАХГҮЙ — үлдэгдлээр нь өөрөө
 *    бодогдоно. Хоёуланг нь гараар шивүүлбэл нийлбэр нь захиалгын
 *    дүнтэй зөрж, сервер татгалзана. Кассчин тооцоо хийх ёсгүй.
 *
 * ⚠️ И-БАРИМТЫГ ЭНД БОДНО (2026-10-06): зээлийн апп орсон бол заавал,
 *    ЗӨВХӨН аппын хэсэгт — дүн нь хуваалтаас хамаарна. Кассчин үнийн
 *    хэсгийг оруулна, нэмэгдэл аппын мөрд нэмэгдэнэ (`chargeParts`).
 *
 * ⚠️ Хүчинтэй эсэхийг `checkPaymentSplit` (@boots/domain) шийднэ —
 *    сервер ЯГ ТЭР функцийг ашигладаг тул энд «болно» гэж үзээд тэнд
 *    татгалзах боломжгүй. Дутуу бол ХООСОН массив буцаана, «Зарах»
 *    товч идэвхгүй болно.
 *
 * ⚠️ Зээлийн аппуудын жагсаалт СЕРВЕРЭЭС (`lookups(PAYMENT_PROVIDER)`),
 *    кодод бэхлэхгүй — шинэ апп гарахад захиалагч өөрөө нэмнэ.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PaymentMethod } from '@boots/schema';
import {
  INSTALLMENT_DOWN_PCT,
  INSTALLMENT_PAY_METHODS,
  VAT_PCT,
  chargeParts,
  checkInstallmentPayments,
  checkPaymentSplit,
  formatMnt,
  installmentSplit,
  moneyInput,
} from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { PAYMENT_LABEL } from '../lib/types';
import { Card, CardTitle, Chip, Field, Input } from './ui';

/**
 * Кассанд гаргах хэлбэрүүд. `VOUCHER`/`MIXED` нь v3 / хураангуй утга.
 * `EXTERNAL_POS` («Гадны касс») НУУГДСАН (2026-09-28, эзэмшигчийн шийдвэр) —
 * тусдаа POS терминал хэрэглэдэггүй. Сервер хүлээн авсаар тул хуучин баримт
 * уншигдана.
 */
const METHODS: PaymentMethod[] = ['CASH', 'CARD', 'QPAY', 'TRANSFER', 'CREDIT_APP', 'INSTALLMENT'];
/** Хоёр дахь хэсэгт хувь зээл байхгүй — хувь зээл нь өөрөө 10% + 90% хуваалт. */
const SPLIT_METHODS = METHODS.filter((m) => m !== 'INSTALLMENT');
const DOWN_METHODS = INSTALLMENT_PAY_METHODS as readonly PaymentMethod[];
/** Кассын чип — мэдрэгч дэлгэцэд арай том (34px, dark glass) */
const CHIP = 'px-3.5 text-meta sm:min-h-[34px]';

export interface PaymentPart {
  method: PaymentMethod;
  providerId?: string;
  amountMnt: string;
}

export interface PaymentChoice {
  /** Хүчинтэй бол мөрүүд, дутуу бол ХООСОН. */
  payments: PaymentPart[];
  /**
   * Сонгосон хэлбэрүүд — дүн бүрдээгүй байсан ч. Касс үүгээр хувь зээлийн
   * талбарууд болон зээлийн аппын баримтыг автоматаар хэвлэхийг шийднэ;
   * `payments`-ийн хоосон массиваас хамааруулбал талбарууд «үсэрнэ».
   */
  methods: PaymentMethod[];
  /** И-баримтын нэмэгдэл — зээлийн аппын хэсэгт эсвэл (сонгосон бол) бүх дүнд */
  vatMnt: number;
  /** Төлөх дүн — суурь + нэмэгдэл */
  total: number;
}

export function PaymentPicker({
  base,
  vat,
  onChange,
}: {
  /** Хөнгөлөлт хассан дүн — нэмэгдэлгүй */
  base: number;
  /** Кассчны «И-баримт» сонголт (зээлийн апп орсон бол үл хамаарна) */
  vat: boolean;
  onChange: (choice: PaymentChoice) => void;
}) {
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [provider, setProvider] = useState('');
  const [split, setSplit] = useState(false);
  const [firstAmount, setFirstAmount] = useState('');
  const [method2, setMethod2] = useState<PaymentMethod>('CREDIT_APP');
  const [provider2, setProvider2] = useState('');
  /** Хувь зээлийн эхний 10%-ийн хэлбэр */
  const [downMethod, setDownMethod] = useState<PaymentMethod>('CASH');

  const providers = useQuery({
    queryKey: ['ref-payment-providers'],
    queryFn: () =>
      api<Envelope<{ id: string; code: string; labelMn: string }[]>>(
        '/ref/lookups?category=PAYMENT_PROVIDER&active=true',
      ),
  });
  const providerList = providers.data?.data ?? [];

  const installment = method === 'INSTALLMENT';
  // Хувь зээл нь өөрөө хуваалт — «хэсэгчлэн төлөх»-тэй хамт хэрэглэхгүй
  const splitOn = split && !installment;
  // Үнийн хэсгүүд (нэмэгдэлгүй) — эхнийхийг кассчин оруулна, хоёр дахь нь үлдэгдэл
  const first = splitOn ? Math.round(Number(firstAmount) || 0) : base;
  const rest = base - first;
  const charge = chargeParts(
    base,
    splitOn ? [{ method, amountMnt: first }, { method: method2, amountMnt: rest }] : [{ method, amountMnt: base }],
    vat,
  );
  // ⚠️ Тоо болгож авна — Decimal объект render бүрд шинэ тул effect-ийн
  //    хамаарал болбол onChange → setState → render → … давталт үүснэ.
  const vatMnt = charge.vatMnt.toNumber();
  const total = charge.payableMnt.toNumber();
  const amount1 = charge.parts[0]!.amountMnt.toNumber();
  const amount2 = charge.parts[1]?.amountMnt.toNumber() ?? 0;
  const loan = installmentSplit(total);
  const down = loan.downMnt.toNumber();
  const principal = loan.principalMnt.toNumber();

  useEffect(() => {
    const part = (m: PaymentMethod, p: string, amount: number): PaymentPart => ({
      method: m,
      amountMnt: moneyInput(amount),
      ...(m === 'CREDIT_APP' && p ? { providerId: p } : {}),
    });
    if (installment) {
      // POS-той ИЖИЛ: эхний 10% + 90% INSTALLMENT, сервер ижил функцээр тулгана
      const parts = [part(downMethod, '', down), part('INSTALLMENT', '', principal)];
      const ok =
        checkPaymentSplit(parts, moneyInput(total)) === null &&
        checkInstallmentPayments(parts, moneyInput(total)) === null;
      onChange({ payments: ok ? parts : [], methods: [downMethod, 'INSTALLMENT'], vatMnt, total });
      return;
    }
    const parts = splitOn
      ? [part(method, provider, amount1), part(method2, provider2, amount2)]
      : [part(method, provider, amount1)];
    onChange({
      payments: checkPaymentSplit(parts, moneyInput(total)) === null ? parts : [],
      methods: splitOn ? [method, method2] : [method],
      vatMnt,
      total,
    });
  }, [installment, downMethod, down, principal, splitOn, method, provider, method2, provider2, amount1, amount2, vatMnt, total, onChange]);

  return (
    <Card>
      <CardTitle
        right={
          installment ? null : (
            <button
              type="button"
              onClick={() => setSplit((v) => !v)}
              className="text-[12.5px] font-medium text-accent-700 hover:underline"
            >
              {split ? 'Нэг хэлбэрээр' : 'Хэсэгчлэн төлөх'}
            </button>
          )
        }
      >
        Төлбөр
      </CardTitle>

      <div className="mt-1 flex flex-col gap-3">
        <Methods value={method} onPick={setMethod} />
        {method === 'CREDIT_APP' ? (
          <Apps list={providerList} value={provider} onPick={setProvider} />
        ) : null}

        {installment ? (
          <>
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-ink-2">Эхний төлөлт {INSTALLMENT_DOWN_PCT}%</span>
              <span className="num font-semibold">{formatMnt(down)}</span>
            </div>
            <Methods value={downMethod} onPick={setDownMethod} options={DOWN_METHODS} />
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-ink-2">Хувь зээл (үлдэгдэл)</span>
              <span className="num font-semibold">{formatMnt(principal)}</span>
            </div>
          </>
        ) : null}

        {splitOn ? (
          <>
            <Field label="Үнээс энэ хэлбэрээр хэдэн ₮">
              <Input
                value={firstAmount}
                onChange={(e) => setFirstAmount(e.target.value)}
                inputMode="decimal"
                className="num text-right text-base"
                placeholder="500000"
              />
            </Field>
            {first > 0 && amount1 > first ? (
              <div className="flex items-baseline justify-between gap-4 text-sm">
                <VatHint extra={amount1 - first} />
                <span className="num font-semibold">{formatMnt(amount1)}</span>
              </div>
            ) : null}
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <span className="flex flex-col">
                <span className="text-ink-2">Үлдэгдэл</span>
                {rest > 0 && amount2 > rest ? <VatHint extra={amount2 - rest} /> : null}
              </span>
              <span className={rest > 0 ? 'num font-semibold' : 'text-[12.5px] text-alert-ink'}>
                {rest > 0 ? formatMnt(amount2) : 'дүнгээ шалгана уу'}
              </span>
            </div>
            <Methods value={method2} onPick={setMethod2} options={SPLIT_METHODS} />
            {method2 === 'CREDIT_APP' ? (
              <Apps list={providerList} value={provider2} onPick={setProvider2} />
            ) : null}
          </>
        ) : null}
      </div>
    </Card>
  );
}

/** Мөрд нэмэгдсэн и-баримт — кассчин тэр хэлбэрээр хэдийг авахаа шууд харна */
function VatHint({ extra }: { extra: number }) {
  return (
    <span className="text-[12.5px] text-ink-3">
      үүнд И-баримт +{VAT_PCT}% · <span className="num">{formatMnt(extra)}</span>
    </span>
  );
}

function Methods({
  value,
  onPick,
  options = METHODS,
}: {
  value: PaymentMethod;
  onPick: (m: PaymentMethod) => void;
  options?: readonly PaymentMethod[];
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((m) => (
        <Chip key={m} active={value === m} onClick={() => onPick(m)} className={CHIP}>
          {PAYMENT_LABEL[m] ?? m}
        </Chip>
      ))}
    </div>
  );
}

function Apps({
  list,
  value,
  onPick,
}: {
  list: { id: string; labelMn: string }[];
  value: string;
  onPick: (id: string) => void;
}) {
  if (list.length === 0) {
    return <p className="text-[12.5px] text-ink-3">Зээлийн аппын жагсаалт татагдсангүй</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((p) => (
        <Chip key={p.id} active={value === p.id} onClick={() => onPick(p.id)} className={CHIP}>
          {p.labelMn}
        </Chip>
      ))}
    </div>
  );
}
